import { Link } from "react-router-dom";
import { ArrowRight, Calculator, FileText, Landmark, Building, Globe, ScrollText, Briefcase, Home as HomeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";

const services = [
  {
    id: "isee",
    title: "ISEE e DSU",
    description: "Compilazione della Dichiarazione Sostitutiva Unica (DSU) per ottenere l'Indicatore della Situazione Economica Equivalente, necessario per accedere a bonus, agevolazioni e servizi pubblici.",
    details: [
      "ISEE ordinario",
      "ISEE universitario",
      "ISEE sociosanitario",
      "ISEE minorenni",
      "ISEE corrente"
    ],
    icon: Calculator,
    link: "/documenti-isee"
  },
  {
    id: "730",
    title: "Modello 730",
    description: "Assistenza completa per la compilazione e trasmissione del modello 730, con verifica delle detrazioni e deduzioni spettanti e calcolo del rimborso o del debito fiscale.",
    details: [
      "730 ordinario",
      "730 precompilato",
      "730 congiunto",
      "Detrazioni e deduzioni",
      "Bonus edilizi"
    ],
    icon: FileText,
    link: "/documenti-730"
  },
  {
    id: "pensioni",
    title: "Pensioni",
    description: "Assistenza per tutte le pratiche pensionistiche, dalla verifica dei requisiti alla presentazione della domanda presso gli enti previdenziali.",
    details: [
      "Pensione di vecchiaia",
      "Pensione anticipata",
      "Pensione di invalidità",
      "Pensione di reversibilità",
      "Assegno sociale",
      "Quota 100/102/103"
    ],
    icon: Landmark,
    link: null
  },
  {
    id: "inps",
    title: "Pratiche INPS",
    description: "Gestione completa delle pratiche INPS: dalla disoccupazione ai bonus familiari, passando per congedi e indennità.",
    details: [
      "NASPI - Disoccupazione",
      "Assegno Unico Universale",
      "Bonus maternità",
      "Congedi parentali",
      "Indennità di malattia",
      "Reddito di Cittadinanza"
    ],
    icon: Building,
    link: null
  },
  {
    id: "immigrazione",
    title: "Immigrazione",
    description: "Assistenza completa per cittadini stranieri: permessi di soggiorno, ricongiungimenti familiari, pratiche di cittadinanza e molto altro.",
    details: [
      "Permesso di soggiorno",
      "Carta di soggiorno",
      "Ricongiungimento familiare",
      "Cittadinanza italiana",
      "Nulla osta lavoro",
      "Conversione permessi"
    ],
    icon: Globe,
    link: null
  },
  {
    id: "successioni",
    title: "Successioni",
    description: "Gestione completa delle pratiche successorie, dalla dichiarazione di successione alla voltura catastale degli immobili ereditati.",
    details: [
      "Dichiarazione di successione",
      "Voltura catastale",
      "Visure ereditarie",
      "Calcolo imposte successione",
      "Successione testamentaria"
    ],
    icon: ScrollText,
    link: null
  },
  {
    id: "lavoro",
    title: "Contratti di Lavoro",
    description: "Assistenza per la gestione dei rapporti di lavoro domestico e agricolo, con compilazione dei contratti e gestione dei contributi.",
    details: [
      "Contratti colf e badanti",
      "Contributi lavoratori domestici",
      "Contratti agricoli stagionali",
      "Vertenze di lavoro"
    ],
    icon: Briefcase,
    link: null
  },
  {
    id: "casa",
    title: "Bonus Casa",
    description: "Consulenza e assistenza per tutti i bonus edilizi: ristrutturazioni, risparmio energetico, mobili e superbonus.",
    details: [
      "Superbonus 110%",
      "Bonus ristrutturazione 50%",
      "Ecobonus",
      "Bonus mobili",
      "Bonus facciate",
      "Cessione del credito"
    ],
    icon: HomeIcon,
    link: null
  }
];

const Servizi = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-400 mb-4 block">
              I Nostri Servizi
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter text-white mb-6">
              Assistenza completa per ogni esigenza
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed">
              Dal modello 730 all'ISEE, dalle pensioni alle pratiche di immigrazione. Siamo qui per semplificare la burocrazia.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8">
            {services.map((service, idx) => (
              <div
                key={service.id}
                className="bg-white border border-slate-200 rounded-2xl p-8 md:p-10 card-hover"
                data-testid={`service-detail-${service.id}`}
              >
                <div className="grid md:grid-cols-12 gap-8 items-start">
                  <div className="md:col-span-8">
                    <div className="flex items-start gap-4 mb-6">
                      <div className="w-14 h-14 bg-blue-900 rounded-xl flex items-center justify-center flex-shrink-0">
                        <service.icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">
                          {service.title}
                        </h2>
                        <p className="text-lg text-slate-600">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-4">
                    <h4 className="text-sm font-semibold tracking-[0.1em] uppercase text-slate-500 mb-4">
                      Include
                    </h4>
                    <ul className="space-y-2">
                      {service.details.map((detail, i) => (
                        <li key={i} className="text-slate-700 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-red-600 rounded-full" />
                          {detail}
                        </li>
                      ))}
                    </ul>
                    {service.link && (
                      <Link to={service.link} className="mt-6 inline-block">
                        <Button 
                          variant="outline" 
                          className="rounded-full"
                          data-testid={`service-link-${service.id}`}
                        >
                          Documenti necessari
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
            Non trovi il servizio che cerchi?
          </h2>
          <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto">
            Contattaci per qualsiasi informazione. Il nostro team è a disposizione per rispondere a tutte le tue domande.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prenota">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8"
                data-testid="servizi-cta-prenota"
              >
                Prenota Appuntamento
              </Button>
            </Link>
            <Link to="/contatti">
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full px-8"
                data-testid="servizi-cta-contatti"
              >
                Contattaci
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Servizi;
